import React, { useEffect, useRef, useState } from "react";
import { Download, Share2, MessageCircle, Twitter, Instagram, Copy, Check } from "lucide-react";
import { useLang } from "@/lib/useLang";
import { getCandidateName, CANVAS_FONTS, STRINGS } from "@/lib/i18n";
import Analytics from "@/lib/analytics";

const COLOR_MAP = {
  saffron: { bg: "#FF9933", ink: "#0A0A0A" },
  green:   { bg: "#138808", ink: "#FFFFFF" },
  red:     { bg: "#E3000F", ink: "#FFFFFF" },
};

const FEED  = { w: 1200, h: 630 };
const STORY = { w: 1080, h: 1920 };

const photoCache = new Map();
function loadImage(url) {
  if (!url) return Promise.resolve(null);
  if (photoCache.has(url)) return photoCache.get(url);
  const promise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
  photoCache.set(url, promise);
  return promise;
}

async function ensureFontsReady(lang) {
  if (!document.fonts) return;
  try {
    if (lang === "ml") {
      await Promise.all([
        document.fonts.load("800 120px 'Baloo Chettan 2'"),
        document.fonts.load("800 92px 'Baloo Chettan 2'"),
        document.fonts.load("800 30px 'Noto Sans Malayalam'"),
      ]);
    } else {
      await Promise.all([
        document.fonts.load("160px Anton"),
        document.fonts.load("260px Anton"),
        document.fonts.load("800 30px 'Work Sans'"),
      ]);
    }
    await document.fonts.ready;
  } catch (e) { /* ignore */ }
}

function drawImageCover(ctx, img, x, y, w, h, focusY = 0.18) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const sRatio = iw / ih;
  const dRatio = w / h;
  let sx = 0, sy = 0, sw = iw, sh = ih;
  if (sRatio > dRatio) {
    sw = ih * dRatio;
    sx = (iw - sw) / 2;
  } else {
    sh = iw / dRatio;
    sy = Math.max(0, Math.min(ih - sh, (ih - sh) * focusY * 2));
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function drawBackground(ctx, W, H, fonts, dict, lang) {
  ctx.fillStyle = "#FDFBF7";
  ctx.fillRect(0, 0, W, H);

  // Dotted grain
  ctx.fillStyle = "rgba(10,10,10,0.06)";
  for (let y = 0; y < H; y += 24) {
    for (let x = 0; x < W; x += 24) {
      ctx.beginPath();
      ctx.arc(x, y, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Outer thick black frame
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(0, 0, W, 16);
  ctx.fillRect(0, H - 16, W, 16);
  ctx.fillRect(0, 0, 16, H);
  ctx.fillRect(W - 16, 0, 16, H);

  // Eyebrow
  ctx.fillStyle = "#404040";
  ctx.font = `800 ${W >= STORY.w ? 28 : 22}px ${fonts.body}`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  const eyebrow = lang === "ml"
    ? `★  ${dict.brand_eyebrow}  ·  ${dict.brand_name}  ★`
    : "★  KERALA · 2026  ·  THE PEOPLE'S POLL  ★";
  ctx.fillText(eyebrow, 60, 56);
}

function drawUrlBar(ctx, W, H, fonts, dict, shareUrl) {
  const barH = W >= STORY.w ? 90 : 70;
  const barY = H - barH - 40;
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(40, barY, W - 80, barH);
  ctx.fillStyle = "#FF9933";
  ctx.font = `800 ${W >= STORY.w ? 34 : 28}px ${fonts.body}`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  const cleanUrl = (shareUrl || "").replace(/^https?:\/\//, "");
  ctx.fillText(`${dict.card_kerala_decides}   →   ${cleanUrl}`, 64, barY + barH / 2);
}

// ---------- FEED layout (1200 × 630) ----------

async function drawFeedReveal(ctx, candidate, shareUrl, lang, celebrate) {
  await ensureFontsReady(lang);
  const photo = await loadImage(candidate.photo);
  const { w: W, h: H } = FEED;

  const c = COLOR_MAP[candidate.color] || COLOR_MAP.saffron;
  const fonts = CANVAS_FONTS[lang] || CANVAS_FONTS.en;
  const dict = STRINGS[lang] || STRINGS.en;
  const displayName = getCandidateName(candidate, lang);
  const headingWeight = lang === "ml" ? "800 " : "";

  drawBackground(ctx, W, H, fonts, dict, lang);

  ctx.fillStyle = "#0A0A0A";
  const headlineSize = lang === "ml" ? 84 : 100;
  ctx.font = `${headingWeight}${headlineSize}px ${fonts.display}`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillText(celebrate ? dict.card_congrats_to : dict.card_i_voted, 60, 90);

  const tileX = W - 60 - 360;
  const tileY = 200;
  const tileW = 360, tileH = 380;

  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(tileX + 14, tileY + 14, tileW, tileH);
  ctx.fillStyle = c.bg;
  ctx.fillRect(tileX, tileY, tileW, tileH);

  const pad = 14;
  if (photo) {
    drawImageCover(ctx, photo, tileX + pad, tileY + pad, tileW - pad * 2, tileH - pad * 2, 0.18);
  } else {
    ctx.fillStyle = c.bg;
    ctx.fillRect(tileX + pad, tileY + pad, tileW - pad * 2, tileH - pad * 2);
    ctx.fillStyle = c.ink;
    ctx.font = `260px ${fonts.display}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(candidate.initials, tileX + tileW / 2, tileY + tileH / 2 + 8);
  }

  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 8;
  ctx.strokeRect(tileX + 4, tileY + 4, tileW - 8, tileH - 8);

  ctx.fillStyle = "#0A0A0A";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  let nameSize = lang === "ml" ? 76 : 92;
  ctx.font = `${headingWeight}${nameSize}px ${fonts.display}`;
  const maxNameW = tileX - 60 - 30;
  while (ctx.measureText(displayName).width > maxNameW && nameSize > 36) {
    nameSize -= 4;
    ctx.font = `${headingWeight}${nameSize}px ${fonts.display}`;
  }
  ctx.fillText(displayName, 60, lang === "ml" ? 360 : 340);

  ctx.fillStyle = "#404040";
  ctx.font = `800 24px ${fonts.body}`;
  ctx.fillText(celebrate ? dict.card_celebrate_subtitle : dict.card_for_cm, 62, lang === "ml" ? 405 : 390);

  ctx.fillStyle = "#E3000F";
  ctx.font = `italic 800 30px ${fonts.body}`;
  ctx.fillText(celebrate ? dict.card_celebrate_invite : dict.card_invite, 60, 490);

  drawUrlBar(ctx, W, H, fonts, dict, shareUrl);
}

async function drawFeedAnon(ctx, allCandidates, shareUrl, lang) {
  await ensureFontsReady(lang);
  const photos = await Promise.all((allCandidates || []).map((c) => loadImage(c.photo)));
  const { w: W, h: H } = FEED;

  const fonts = CANVAS_FONTS[lang] || CANVAS_FONTS.en;
  const dict = STRINGS[lang] || STRINGS.en;
  const headingWeight = lang === "ml" ? "800 " : "";

  drawBackground(ctx, W, H, fonts, dict, lang);

  ctx.fillStyle = "#0A0A0A";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  const h1 = lang === "ml" ? 90 : 110;
  ctx.font = `${headingWeight}${h1}px ${fonts.display}`;
  ctx.fillText(dict.card_anon_headline, 60, 90);

  ctx.fillStyle = "#E3000F";
  ctx.fillText(dict.card_kerala_decides + ".", 60, 90 + (lang === "ml" ? 120 : 110));

  ctx.fillStyle = "#404040";
  ctx.font = `800 24px ${fonts.body}`;
  ctx.fillText(dict.card_anon_subtitle, 62, 360);

  const candidates = allCandidates || [];
  const tileSize = 130, gap = 24;
  const rowW = tileSize * 3 + gap * 2;
  const rowX = (W - rowW) / 2;
  const rowY = 410;

  candidates.forEach((c, i) => {
    const color = (COLOR_MAP[c.color] || COLOR_MAP.saffron).bg;
    const x = rowX + i * (tileSize + gap);
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(x + 8, rowY + 8, tileSize, tileSize);
    ctx.fillStyle = color;
    ctx.fillRect(x, rowY, tileSize, tileSize);
    const p = 8;
    if (photos[i]) {
      ctx.save();
      try { ctx.filter = "grayscale(1) contrast(1.05)"; } catch (e) { /* ignore */ }
      drawImageCover(ctx, photos[i], x + p, rowY + p, tileSize - p * 2, tileSize - p * 2, 0.18);
      ctx.restore();
    }
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 4;
    ctx.strokeRect(x + 2, rowY + 2, tileSize - 4, tileSize - 4);
  });

  drawUrlBar(ctx, W, H, fonts, dict, shareUrl);
}

// ---------- STORY layout (1080 × 1920) ----------

async function drawStoryReveal(ctx, candidate, shareUrl, lang, celebrate) {
  await ensureFontsReady(lang);
  const photo = await loadImage(candidate.photo);
  const { w: W, h: H } = STORY;

  const c = COLOR_MAP[candidate.color] || COLOR_MAP.saffron;
  const fonts = CANVAS_FONTS[lang] || CANVAS_FONTS.en;
  const dict = STRINGS[lang] || STRINGS.en;
  const displayName = getCandidateName(candidate, lang);
  const headingWeight = lang === "ml" ? "800 " : "";

  drawBackground(ctx, W, H, fonts, dict, lang);

  // Headline "I VOTED FOR" or "CONGRATS TO" — left aligned
  ctx.fillStyle = "#0A0A0A";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  const h1 = lang === "ml" ? 130 : 170;
  ctx.font = `${headingWeight}${h1}px ${fonts.display}`;
  ctx.fillText(celebrate ? dict.card_congrats_to : dict.card_i_voted, 70, 160);

  // Big square photo tile, centered horizontally
  const tileSize = 820;
  const tileX = (W - tileSize) / 2;
  const tileY = 470;

  // Drop shadow
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(tileX + 22, tileY + 22, tileSize, tileSize);
  // Brand-color frame
  ctx.fillStyle = c.bg;
  ctx.fillRect(tileX, tileY, tileSize, tileSize);

  const pad = 22;
  if (photo) {
    drawImageCover(ctx, photo, tileX + pad, tileY + pad, tileSize - pad * 2, tileSize - pad * 2, 0.20);
  } else {
    ctx.fillStyle = c.bg;
    ctx.fillRect(tileX + pad, tileY + pad, tileSize - pad * 2, tileSize - pad * 2);
    ctx.fillStyle = c.ink;
    ctx.font = `460px ${fonts.display}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(candidate.initials, tileX + tileSize / 2, tileY + tileSize / 2 + 12);
  }

  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 12;
  ctx.strokeRect(tileX + 6, tileY + 6, tileSize - 12, tileSize - 12);

  // Candidate name — centered below photo, auto-fit
  ctx.fillStyle = "#0A0A0A";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  let nameSize = lang === "ml" ? 110 : 130;
  ctx.font = `${headingWeight}${nameSize}px ${fonts.display}`;
  const maxNameW = W - 140;
  while (ctx.measureText(displayName).width > maxNameW && nameSize > 48) {
    nameSize -= 6;
    ctx.font = `${headingWeight}${nameSize}px ${fonts.display}`;
  }
  ctx.fillText(displayName, W / 2, tileY + tileSize + 60);

  // Subtitle
  ctx.fillStyle = "#404040";
  ctx.font = `800 36px ${fonts.body}`;
  ctx.fillText(celebrate ? dict.card_celebrate_subtitle : dict.card_for_cm, W / 2, tileY + tileSize + 60 + nameSize + 18);

  // Accent invite
  ctx.fillStyle = "#E3000F";
  ctx.font = `italic 800 44px ${fonts.body}`;
  ctx.fillText(celebrate ? dict.card_celebrate_invite : dict.card_invite, W / 2, tileY + tileSize + 60 + nameSize + 18 + 80);

  drawUrlBar(ctx, W, H, fonts, dict, shareUrl);
}

async function drawStoryAnon(ctx, allCandidates, shareUrl, lang) {
  await ensureFontsReady(lang);
  const photos = await Promise.all((allCandidates || []).map((c) => loadImage(c.photo)));
  const { w: W, h: H } = STORY;

  const fonts = CANVAS_FONTS[lang] || CANVAS_FONTS.en;
  const dict = STRINGS[lang] || STRINGS.en;
  const headingWeight = lang === "ml" ? "800 " : "";

  drawBackground(ctx, W, H, fonts, dict, lang);

  // Stacked headline
  ctx.fillStyle = "#0A0A0A";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  const h1 = lang === "ml" ? 130 : 170;
  ctx.font = `${headingWeight}${h1}px ${fonts.display}`;
  ctx.fillText(dict.card_anon_headline, 70, 160);

  ctx.fillStyle = "#E3000F";
  ctx.fillText(dict.card_kerala_decides + ".", 70, 160 + (lang === "ml" ? 170 : 170));

  // Subtitle (centered)
  ctx.fillStyle = "#404040";
  ctx.font = `800 36px ${fonts.body}`;
  ctx.textAlign = "center";
  ctx.fillText(dict.card_anon_subtitle, W / 2, 580);

  // Three candidate photos stacked vertically
  const candidates = allCandidates || [];
  const tileSize = 280;
  const gap = 32;
  const totalH = tileSize * 3 + gap * 2;
  // Layout horizontally in 3 columns? Use horizontal row with bigger tiles since portrait gives width too.
  const rowW = tileSize * 3 + gap * 2;
  const rowX = (W - rowW) / 2;
  const rowY = 700;

  candidates.forEach((c, i) => {
    const color = (COLOR_MAP[c.color] || COLOR_MAP.saffron).bg;
    const x = rowX + i * (tileSize + gap);
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(x + 12, rowY + 12, tileSize, tileSize);
    ctx.fillStyle = color;
    ctx.fillRect(x, rowY, tileSize, tileSize);
    const p = 14;
    if (photos[i]) {
      ctx.save();
      try { ctx.filter = "grayscale(1) contrast(1.05)"; } catch (e) { /* ignore */ }
      drawImageCover(ctx, photos[i], x + p, rowY + p, tileSize - p * 2, tileSize - p * 2, 0.18);
      ctx.restore();
    }
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 6;
    ctx.strokeRect(x + 3, rowY + 3, tileSize - 6, tileSize - 6);
  });
  void totalH;

  // Big accent invite below the row
  ctx.fillStyle = "#E3000F";
  ctx.font = `italic 800 56px ${fonts.body}`;
  ctx.textAlign = "center";
  ctx.fillText(dict.card_invite, W / 2, rowY + tileSize + 80);

  drawUrlBar(ctx, W, H, fonts, dict, shareUrl);
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}

export const ShareCard = ({ candidate, allCandidates, pollState }) => {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);   // default: anonymous
  const [format, setFormat] = useState("feed");  // default: feed
  const [igTip, setIgTip] = useState(false);
  const { t, lang } = useLang();

  const frozen = !!pollState?.frozen;
  const announcedCm = pollState?.announced_cm_name || "";
  const celebrate = frozen && !!announcedCm; // master switch for celebration mode

  // In celebrate mode the poster ALWAYS shows the announced CM (reveal-style),
  // never the user's personal vote. The URL is forced to the canonical site.
  const effectiveReveal = celebrate ? true : reveal;
  const effectiveCandidate = candidate;
  const shareUrl = celebrate
    ? "https://www.thenextcm.com"
    : (typeof window !== "undefined" ? window.location.origin : "");
  const dict = STRINGS[lang] || STRINGS.en;
  const shareText = celebrate
    ? dict.share_text_congrats(announcedCm)
    : reveal
      ? dict.share_text(getCandidateName(candidate, lang))
      : dict.share_text_anon();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dims = format === "story" ? STORY : FEED;
    canvas.width = dims.w;
    canvas.height = dims.h;
    const ctx = canvas.getContext("2d");
    if (format === "story") {
      if (effectiveReveal) drawStoryReveal(ctx, effectiveCandidate, shareUrl, lang, celebrate);
      else drawStoryAnon(ctx, allCandidates || [effectiveCandidate], shareUrl, lang);
    } else {
      if (effectiveReveal) drawFeedReveal(ctx, effectiveCandidate, shareUrl, lang, celebrate);
      else drawFeedAnon(ctx, allCandidates || [effectiveCandidate], shareUrl, lang);
    }
  }, [effectiveCandidate, allCandidates, shareUrl, lang, effectiveReveal, format, celebrate]);

  const filename = () => {
    if (celebrate) {
      const slug = (announcedCm || "kerala-cm")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      return `congrats-${slug}-${format}.png`;
    }
    return `kerala-decides-${reveal ? candidate.id : "anonymous"}-${format}.png`;
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await canvasToBlob(canvasRef.current);
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      Analytics.shareClick("save", reveal ? "reveal" : "anon", format);
    } finally {
      setBusy(false);
    }
  };

  const handleInstagram = async () => {
    Analytics.shareClick("instagram", reveal ? "reveal" : "anon", format);
    await handleDownload();
    setIgTip(true);
    setTimeout(() => {
      try {
        const a = document.createElement("a");
        a.href = "instagram://library?LocalIdentifier=";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) { /* ignore */ }
    }, 400);
    setTimeout(() => setIgTip(false), 8000);
  };

  const handleNativeShare = async () => {
    setBusy(true);
    try {
      let file = null;
      try {
        const blob = await canvasToBlob(canvasRef.current);
        if (blob) file = new File([blob], filename(), { type: "image/png" });
      } catch (e) {
        console.warn("canvas blob unavailable for share", e);
      }
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: t("card_kerala_decides"),
          text: `${shareText} ${shareUrl}`,
          files: [file],
        });
        Analytics.shareClick("native", reveal ? "reveal" : "anon", format);
      } else if (navigator.share) {
        await navigator.share({
          title: t("card_kerala_decides"),
          text: `${shareText} ${shareUrl}`,
          url: shareUrl,
        });
        Analytics.shareClick("native", reveal ? "reveal" : "anon", format);
      }
    } catch (e) {
      if (e?.name !== "AbortError") console.warn("share failed", e);
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      Analytics.shareClick("copy", reveal ? "reveal" : "anon", format);
    } catch (e) { /* ignore */ }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const supportsShare = typeof navigator !== "undefined" && !!navigator.share;

  // Constrain story preview height so it doesn't dominate the page
  const previewStyle = format === "story"
    ? { width: "auto", maxHeight: "560px", margin: "0 auto", display: "block" }
    : { width: "100%", height: "auto", display: "block" };

  return (
    <section className="brutal-card p-5 md:p-8 fade-up" data-testid="share-card-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
            {frozen && announcedCm ? "★ CONGRATULATE THE NEW CM ★" : t("spread_word")}
          </p>
          <h3
            className="font-display uppercase leading-none mt-2"
            style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
            data-testid="share-card-title"
          >
            {frozen && announcedCm
              ? dict.congrats_heading_for(announcedCm)
              : t("share_your_vote")}
          </h3>
        </div>
        <p className="text-sm md:text-base font-medium max-w-md text-neutral-700">
          {frozen && announcedCm ? dict.congrats_help : t("share_help")}
          {!frozen && (
            <>
              <br />
              <span className="font-bold" style={{ color: "#0A0A0A" }}>{t("share_tag_leaders")}</span>
            </>
          )}
        </p>
      </div>

      {/* Reveal toggle — irrelevant in celebrate mode (poster always shows the new CM) */}
      {!celebrate && (
        <div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-5 border-b-2 border-dashed border-neutral-300"
          data-testid="reveal-toggle-row"
        >
          <div>
            <p className="text-xs uppercase font-bold tracking-[0.25em] text-neutral-600">
              {t("reveal_section_label")}
            </p>
            <p className="text-sm font-medium text-neutral-700 mt-1 max-w-md">
              {t("anon_help")}
            </p>
          </div>
          <div
            className="lang-toggle shrink-0"
            role="group"
            aria-label={t("reveal_section_label")}
            data-testid="reveal-toggle"
          >
            <button type="button" aria-pressed={!reveal} onClick={() => { Analytics.revealToggle("anon"); setReveal(false); }} data-testid="reveal-off-btn">
              {t("anon_label")}
            </button>
            <button type="button" aria-pressed={reveal} onClick={() => { Analytics.revealToggle("reveal"); setReveal(true); }} data-testid="reveal-on-btn">
              {t("reveal_label")}
            </button>
          </div>
        </div>
      )}

      {/* Format toggle */}
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-5 border-b-2 border-dashed border-neutral-300"
        data-testid="format-toggle-row"
      >
        <div>
          <p className="text-xs uppercase font-bold tracking-[0.25em] text-neutral-600">
            {t("format_section_label")}
          </p>
          <p className="text-sm font-medium text-neutral-700 mt-1 max-w-md">
            {t("format_help")}
          </p>
        </div>
        <div
          className="lang-toggle shrink-0"
          role="group"
          aria-label={t("format_section_label")}
          data-testid="format-toggle"
        >
          <button type="button" aria-pressed={format === "feed"} onClick={() => { Analytics.formatToggle("feed"); setFormat("feed"); }} data-testid="format-feed-btn">
            {t("format_feed")}
          </button>
          <button type="button" aria-pressed={format === "story"} onClick={() => { Analytics.formatToggle("story"); setFormat("story"); }} data-testid="format-story-btn">
            {t("format_story")}
          </button>
        </div>
      </div>

      <div
        className={`border-4 border-black overflow-hidden ${format === "story" ? "flex justify-center bg-neutral-50" : ""}`}
        data-testid="share-card-preview"
      >
        <canvas ref={canvasRef} aria-label={shareText} style={previewStyle} />
      </div>

      <div
        className={`grid grid-cols-2 gap-3 mt-6 ${supportsShare ? "md:grid-cols-6" : "md:grid-cols-5"}`}
      >
        <button className="brutal-btn" onClick={handleDownload} disabled={busy} data-testid="share-download-btn">
          <Download size={18} strokeWidth={3} className="mr-2" /> {t("save")}
        </button>

        {supportsShare && (
          <button className="brutal-btn" onClick={handleNativeShare} disabled={busy} data-testid="share-native-btn">
            <Share2 size={18} strokeWidth={3} className="mr-2" /> {t("share")}
          </button>
        )}

        <a className="brutal-btn" href={waUrl} target="_blank" rel="noopener noreferrer"
           onClick={() => Analytics.shareClick("whatsapp", reveal ? "reveal" : "anon", format)}
           style={{ background: "#138808", borderColor: "#0A0A0A" }} data-testid="share-whatsapp-btn">
          <MessageCircle size={18} strokeWidth={3} className="mr-2" /> {t("whatsapp")}
        </a>

        <a className="brutal-btn" href={xUrl} target="_blank" rel="noopener noreferrer"
           onClick={() => Analytics.shareClick("x", reveal ? "reveal" : "anon", format)}
           data-testid="share-twitter-btn">
          <Twitter size={18} strokeWidth={3} className="mr-2" /> {t("post_x")}
        </a>

        <button className="brutal-btn" onClick={handleInstagram} disabled={busy} data-testid="share-instagram-btn"
          style={{
            background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
            color: "#FFFFFF",
            borderColor: "#0A0A0A",
          }}
        >
          <Instagram size={18} strokeWidth={3} className="mr-2" /> {t("instagram")}
        </button>

        <button className="brutal-btn" onClick={handleCopy} data-testid="share-copy-btn"
          style={copied ? { background: "#FF9933", color: "#0A0A0A" } : undefined}>
          {copied ? <Check size={18} strokeWidth={3} className="mr-2" /> : <Copy size={18} strokeWidth={3} className="mr-2" />}
          {copied ? t("copied") : t("copy_link")}
        </button>
      </div>

      {igTip && (
        <div
          className="mt-4 border-4 border-black p-4 fade-up"
          style={{ background: "#FFF7E6" }}
          role="status"
          data-testid="instagram-tip"
        >
          <p className="text-sm font-bold leading-relaxed" style={{ color: "#0A0A0A" }}>
            <Instagram size={16} strokeWidth={3} className="inline mr-2 -mt-1" />
            {t("instagram_tip")}
          </p>
        </div>
      )}
    </section>
  );
};

export default ShareCard;
